import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Snackbar,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from "@mui/material";
import { DropzoneArea } from "material-ui-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import {
  clearUpdateStatus,
  createReview,
  resetReview
} from "../../redux/slices/review/review";
import LeftNavigationBar from "../../navbars/LeftNavigationBar";
import { HelpOutline } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

const ReviewAddForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [cookies] = useCookies(["token"]);
  const theme = useTheme();

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    review: "",
    ratings: "",
    type: "", // "institution" or "company"
    role: "",
    company: "",
    institution: "",
  });

  const [profile, setProfile] = useState(null);
  const [video, setVideo] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const { loading, error, isSuccess } = useSelector(
    (state) => state.reviews.reviews
  );

  const [touchedFields, setTouchedFields] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isSuccess) {
      dispatch(resetReview());
    }
  }, [isSuccess, dispatch]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validation logic
    let fieldError = "";
    
    if (name === "name" && !/^[a-zA-Z\s]*$/.test(value)) {
      fieldError = "Name must contain only letters";
    } else if (name === "review" && !/^[a-zA-Z\s]*$/.test(value)) {
      fieldError = "Review must contain only letters";
    } else if (name === "ratings") {
      if (!/^\d+(\.\d{1,2})?$/.test(value)) {
        fieldError = "Rating must be a number with up to 2 decimal places";
      } else if (parseFloat(value) > 5) {
        fieldError = "Rating must be 5 or below";
      }
    }
    
    setErrors(prev => ({ ...prev, [name]: fieldError }));
    setTouchedFields(prev => ({ ...prev, [name]: true }));
  };

  const handleImageChange = (files) => {
    setProfile(files);
    setTouchedFields(prev => ({ ...prev, image: true }));
    setErrors(prev => ({ ...prev, image: "" }));
  };

  const handleVideoChange = (files) => {
    if (files.length > 0) {
      const file = files[0];
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes

      if (file.size > maxSize) {
        setErrors(prev => ({
          ...prev,
          video: "Video file size must be 10MB or less"
        }));
        setVideo(null);
        return;
      }

      setVideo(files);
      setTouchedFields(prev => ({ ...prev, video: true }));
      setErrors(prev => ({ ...prev, video: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Required fields validation
    const requiredFields = ["name", "ratings", "review", "type", "role"];
    let hasErrors = false;
    
    // Add conditional required fields based on type
    if (formData.type === "institution") {
      requiredFields.push("institution");
    } else if (formData.type === "company") {
      requiredFields.push("company");
    }
    
    const newErrors = { ...errors };
    
    requiredFields.forEach(field => {
      if (!formData[field] || !formData[field].trim()) {
        newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
        hasErrors = true;
      }
    });
    
    setErrors(newErrors);
    
    if (hasErrors) return;

    // Create form data for submission
    const submitFormData = new FormData();
    
    // Add all form fields to the FormData
    Object.keys(formData).forEach(key => {
      if (formData[key]) {
        submitFormData.append(key, formData[key]);
      }
    });

    // Add files if they exist
    if (profile) {
      for (let i = 0; i < profile.length; i++) {
        submitFormData.append("profile", profile[i]);
      }
    }

    if (video) {
      for (let i = 0; i < video.length; i++) {
        submitFormData.append("video", video[i]);
      }
    }

    try {
      const result = await dispatch(
        createReview({ token: cookies.token, formData: submitFormData })
      ).unwrap();
      
      if (result) {
        setSubmitSuccess(true);
      }
    } catch (err) {
      console.error("Failed to create review:", err);
    }
  };

  useEffect(() => {
    if (submitSuccess) {
      setTimeout(() => {
        dispatch(clearUpdateStatus());
        navigate("/Review-control");
      }, 2000);
    }
  }, [submitSuccess, navigate, dispatch]);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <LeftNavigationBar
      Content={
        <Container component="main" maxWidth="md">
          <Snackbar
            open={submitSuccess}
            autoHideDuration={2000}
            onClose={() => setSubmitSuccess(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity="success" variant="filled">Review created successfully</Alert>
          </Snackbar>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            mt={2}
            mb={2}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBack}
            >
              Back
            </Button>
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              flex: 1
            }}>
              <Typography
                variant="h4"
                sx={{
                  position: "relative",
                  padding: 0,
                  margin: 0,
                  fontWeight: 300,
                  fontSize: { xs: "32px", sm: "40px" },
                  color: "#747474",
                  textAlign: "center",
                  textTransform: "uppercase",
                  paddingBottom: "5px",
                  "&::before": {
                    content: '""',
                    width: "28px",
                    height: "5px",
                    display: "block",
                    position: "absolute",
                    bottom: "3px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "#747474",
                  },
                  "&::after": {
                    content: '""',
                    width: "100px",
                    height: "1px",
                    display: "block",
                    position: "relative",
                    marginTop: "5px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "#747474",
                  },
                }}
              >
                Testimonial Add Form
              </Typography>
              <Tooltip
                title="Add a new testimonial from an institution or company"
                arrow
              >
                <HelpOutline
                  sx={{ color: "#747474", fontSize: "24px", cursor: "pointer", ml: 1 }}
                />
              </Tooltip>
            </Box>
          </Box>
          <form
            onSubmit={handleSubmit}
            style={{
              border: "2px dotted #D3D3D3",
              padding: "20px",
              marginTop: "20px",
              borderRadius: "8px",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  variant="outlined"
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Ratings (1-5)"
                  name="ratings"
                  type="number"
                  value={formData.ratings}
                  onChange={handleChange}
                  variant="outlined"
                  error={!!errors.ratings}
                  helperText={errors.ratings}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="type-label">Type</InputLabel>
                  <Select
                    labelId="type-label"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    label="Type"
                    error={!!errors.type}
                  >
                    <MenuItem value="">Select Type</MenuItem>
                    <MenuItem value="institution">Institution</MenuItem>
                    <MenuItem value="company">Company</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  variant="outlined"
                  error={!!errors.role}
                  helperText={errors.role}
                  required
                />
              </Grid>

              {formData.type === "institution" && (
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Institution Name"
                    name="institution"
                    value={formData.institution}
                    onChange={handleChange}
                    variant="outlined"
                    error={!!errors.institution}
                    helperText={errors.institution}
                    required
                  />
                </Grid>
              )}

              {formData.type === "company" && (
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    variant="outlined"
                    error={!!errors.company}
                    helperText={errors.company}
                    required
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Review"
                  name="review"
                  value={formData.review}
                  multiline
                  rows={4}
                  onChange={handleChange}
                  variant="outlined"
                  error={!!errors.review}
                  helperText={errors.review}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <DropzoneArea
                  onChange={handleImageChange}
                  acceptedFiles={["image/*"]}
                  filesLimit={5}
                  dropzoneText="Upload Profile Image"
                  showPreviewsInDropzone
                  showPreviews
                  maxFileSize={5 * 1024 * 1024} // 5MB
                />
                {errors.image && (
                  <Typography color="error" variant="caption">
                    {errors.image}
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <DropzoneArea
                  onChange={handleVideoChange}
                  acceptedFiles={["video/*"]}
                  filesLimit={1}
                  maxFileSize={10 * 1024 * 1024} // 10MB
                  dropzoneText="Upload a Video (Max: 10MB)"
                  showPreviewsInDropzone
                  showPreviews
                />
                {errors.video && (
                  <Typography color="error" variant="caption">
                    {errors.video}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    display: "block",
                    marginLeft: "auto",
                    marginRight: "auto",
                    mt: 3,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                >
                  {loading ? "Submitting..." : "Submit Testimonial Data"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Container>
      }
    />
  );
};

export default ReviewAddForm;